import { JSX, useEffect, useMemo, useRef, useState } from 'react';

// components

import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtText } from '@/components/atoms/at-text/at-text';
import { MlExteriorDrawer } from '@/components/molecules/ml-exterior-drawer/ml-exterior-drawer';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtDropdown } from '@/components/atoms/at-dropdown/at-dropdown';
import { AdvancedFiltersModal } from './advanced-filters/AdvancedFiltersModal';
import { AdvancedFiltersBody } from './advanced-filters/AdvancedFiltersBody';
import { ControlsHeader } from './ControlsHeader';
import { FloorplanOptions } from './FloorplanOptions';
import { FloorplanSelects } from './FloorplanSelects';
import { ExteriorAndPriceSelects } from './ExteriorAndPriceSelects';

// types
import { EIcons } from '@/components/atoms/at-icon/icons';
import { IHousePlanHit } from '@/lib/search/types';
import {
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { IModalRefProps } from '@/components/molecules/ml-modal/ml-modal.types';
import { RangeKey } from './types';
import {
  IFilterConfig,
  IFilterControlProps,
} from '../../or-filtered-grid.types';

// hooks
import { useSetLocationModal } from '@/context/setLocationContext';
import { useLocation } from '@/context/locationContext';
import useBreakpoint from '@/hooks/useBreakpoint';

// utils
import { sendZeroResultsGTMEvent } from '@/components/helpers/analytics/gtm';

export const HousePlansControls = ({
  config,
  filterValues,
  setFilter,
  resetFilters,
  totalResults,
  filteredResults,
  sort,
  setSort,
  sortOptions,
  tooltipText,
  scrollListData,
}: IFilterControlProps): JSX.Element => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [wereFiltersUsed, setWereFiltersUsed] = useState(false);
  const [filtersInvalid, setFiltersInvalid] = useState(false);

  const { handleOpen } = useSetLocationModal();
  const { studioId, currentBuildStudio, selectedText } = useLocation();

  // Advanced Filters Modal
  const modalRef = useRef<IModalRefProps | null>(null);
  const handleModal = () => {
    modalRef?.current?.handleOpen?.();
  };

  const bp = useBreakpoint();

  const hasLocation = !!currentBuildStudio?.Id;

  const onMobileFiltersClick = () => {
    setIsDrawerOpen(true);
  };

  const configMap = useMemo(() => {
    return Object.fromEntries(config.map((f) => [f.key, f])) as Record<
      string,
      IFilterConfig<IHousePlanHit>
    >;
  }, [config]);

  const { checkboxConfigs, textConfigs } = useMemo(() => {
    const checks: IFilterConfig<IHousePlanHit>[] = [];
    const texts: IFilterConfig<IHousePlanHit>[] = [];
    for (const item of config)
      (item.type === 'checkbox' ? checks : texts).push(item);
    return { checkboxConfigs: checks, textConfigs: texts };
  }, [config]);

  const onDrawerClose = () => {
    // if Advanced Filter is invalid -- scroll to error and early return
    if (filtersInvalid) {
      const el = document.querySelector<HTMLElement>('[aria-invalid="true"]');

      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
      return;
    }
    setIsDrawerOpen(false);
  };

  const onSortChange = (val: string) => {
    setSort(val);

    const sortAnnouncement = document.getElementById('sort-announcement');
    if (sortAnnouncement) {
      sortAnnouncement.textContent = `Sorted by ${val}`;
    }
  };

  const onReset = () => {
    resetFilters();
    setWereFiltersUsed(false);

    if (bp === 'xs' || bp === 'sm') {
      onDrawerClose();
    }

    const resetAnnouncement = document.getElementById('reset-announcement');
    if (resetAnnouncement) {
      resetAnnouncement.textContent = 'Filters have been reset.';
    }
  };

  useEffect(() => {
    if (filterValues && wereFiltersUsed && filteredResults === 0) {
      sendZeroResultsGTMEvent(filterValues, 'Home Plans LP');
    }
  }, [filterValues, filteredResults, wereFiltersUsed]);

  // Advanced Filters Drawer State
  const advancedFilterValues = () => {
    return {
      MinDepth: String(filterValues.MinDepth ?? ''),
      MaxDepth: String(filterValues.MaxDepth ?? ''),
      MinWidth: String(filterValues.MinWidth ?? ''),
      MaxWidth: String(filterValues.MaxWidth ?? ''),
    };
  };

  // Advanced Filters Drawer State
  const onChange = (key: RangeKey, value: string) => {
    setFilter(key, value);
    setWereFiltersUsed(true);
  };

  // Advanced Filters onBlur
  const onBlur = (key: RangeKey, value: string) => {
    setFilter(key, value);
  };

  return (
    <>
      <AdvancedFiltersModal
        modalRef={modalRef}
        configData={textConfigs}
        filterValues={filterValues}
        setFilter={setFilter}
        scrollListData={scrollListData}
      />
      <div className="w-full rounded-30 bg-skin-secondary py-6 shadow-card sm:px-8 lg:px-20 lg:py-15">
        {/* header + location selector - row 1 */}
        <div className="flex flex-col md:mb-5 lg:flex-row">
          <ControlsHeader
            currentBuildStudio={currentBuildStudio}
            studioId={studioId}
            selectedText={selectedText}
            handleOpen={handleOpen}
          />
        </div>
        {/* home plan filter selects - row 2 */}
        <div className="hidden sm:block">
          <div className="mb-8 flex flex-col xl:flex-row xl:space-x-5">
            {/* left container */}
            <div className="w-full lg:w-3/4">
              <FloorplanSelects
                configMap={configMap}
                filterValues={filterValues}
                setFilter={setFilter}
                setWereFiltersUsed={setWereFiltersUsed}
              />
            </div>
            {/*  right container */}
            <div className="w-full md:mt-5 lg:w-1/2 xl:mt-0">
              <ExteriorAndPriceSelects
                configMap={configMap}
                filterValues={filterValues}
                setFilter={setFilter}
                hasLocation={hasLocation}
                tooltipText={tooltipText}
                setWereFiltersUsed={setWereFiltersUsed}
              />
            </div>
          </div>

          {/* home plan filter checkboxes + reset button - row 3 */}
          <div className="flex flex-col justify-between md:flex-row">
            {/* checkboxes */}
            <FloorplanOptions
              filterValues={filterValues}
              setFilter={setFilter}
              setWereFiltersUsed={setWereFiltersUsed}
              checkboxConfigs={checkboxConfigs}
            />
            {/* buttons */}
            <div className="mt-12 flex gap-5">
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                onClick={() => handleModal()}
                label="Advanced Filters"
                type={EClickableTypes.LINK}
                interactLocation="house-plan-controls"
              />
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                onClick={onReset}
                label="Reset Filters"
                type={EClickableTypes.LINK}
                interactLocation="house-plan-controls"
              />
            </div>
          </div>
        </div>
      </div>

      {/* mobile filters button + drawer */}
      <div className="block sm:hidden">
        <button
          className="mt-5 flex w-full items-center justify-between rounded-30 bg-skin-secondary px-8 py-4"
          onClick={onMobileFiltersClick}
        >
          <AtText>Filters</AtText>
          <AtIcon icon={EIcons.FILTER} />
        </button>
        <MlExteriorDrawer onClose={onDrawerClose} isOpen={isDrawerOpen}>
          <div className="px-5">
            <FloorplanSelects
              configMap={configMap}
              filterValues={filterValues}
              setFilter={setFilter}
              onReset={onReset}
              setWereFiltersUsed={setWereFiltersUsed}
            />
            <ExteriorAndPriceSelects
              configMap={configMap}
              filterValues={filterValues}
              setFilter={setFilter}
              hasLocation={!!currentBuildStudio?.Id}
              setWereFiltersUsed={setWereFiltersUsed}
            />
            <AdvancedFiltersBody
              values={advancedFilterValues()}
              configData={textConfigs}
              onChange={onChange}
              onBlur={onBlur}
              onValidityChange={setFiltersInvalid}
              scrollListData={scrollListData}
            />
            <FloorplanOptions
              checkboxConfigs={checkboxConfigs}
              filterValues={filterValues}
              setFilter={setFilter}
              setWereFiltersUsed={setWereFiltersUsed}
            />
          </div>
        </MlExteriorDrawer>
      </div>
      {/* results tally + sort */}
      <div className="mt-5 flex items-center justify-between">
        <div
          className="flex w-1/2 flex-col md:flex-row md:items-center"
          aria-label={`${filteredResults} Results of ${totalResults} total plans}`}
        >
          <AtHeader
            variant={bp === 'xs' || bp === 'sm' ? 'p' : 'h4'}
            element="p"
            className="m-0"
          >
            {filteredResults} Results
          </AtHeader>
          <AtText className="m-0 text-skin-tertiary md:ml-2">
            of {totalResults} plans
          </AtText>
        </div>
        <div
          id="reset-announcement"
          className="sr-only"
          aria-live="assertive"
        ></div>

        <div
          className="sr-only"
          id="sort-announcement"
          aria-live="assertive"
        ></div>
        <div>
          <AtDropdown
            icon={EIcons.SORT}
            label="Sort by"
            value={sort}
            onChange={(value) =>
              onSortChange(Array.isArray(value) ? value[0] : value)
            }
            options={sortOptions.map(({ label, value }) => ({
              label,
              value,
            }))}
          />
        </div>
      </div>
    </>
  );
};
